import React, { useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useProject4TWSourceSettingsText,
  useProject4TWSourceSettingsViewModel,
} from "@onetasklist/shared/sources/project4tw";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface Project4TWSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const Project4TWSourceSettings: React.FC<
  Project4TWSourceSettingsProps
> = ({ editingConnection, onUpdate, sectionOffset }) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useProject4TWSourceSettingsText(t);
  const vm = useProject4TWSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [projectsQuery, setProjectsQuery] = useState("");
  const [bucketsQuery, setBucketsQuery] = useState("");
  const [doneBucketQuery, setDoneBucketQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <>
      {!vm.isAuthorisedProfileReady && (
        <Text>{text.unauthorisedWarningText}</Text>
      )}

      {vm.isAuthorisedProfileReady && (
        <SettingsSectionCard
          sectionOffset={sectionOffset}
          title={t(
            "board.connections.catalogue.sourceSettings.msproject4tw.sectionProjects",
          )}
        >
          {vm.environmentDisplayName && vm.environmentUrl && (
            <Field orientation="horizontal" label={text.environmentLabelText}>
              <a href={vm.environmentUrl} target="_blank" rel="noreferrer">
                {vm.environmentDisplayName}
              </a>
            </Field>
          )}

          <Field
            orientation="horizontal"
            label={text.projectFilterLabelText}
            hint={text.projectFilterHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedProjectIds}
              query={projectsQuery}
              placeholder={text.projectFilterPlaceholderText}
              options={vm.projects.map((project) => ({
                id: project.msdyn_projectid,
                label: project.msdyn_subject ?? project.msdyn_projectid,
              }))}
              onQueryChange={setProjectsQuery}
              onSelectionChange={vm.onSelectProjects}
              minQueryLength={0}
              typeToSearchText={text.projectFilterHintText}
              isLoading={!vm.projectsLoaded}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.assignedToMeLabelText}
            hint={text.assignedToMeHintText}
          >
            <Checkbox checked disabled />
          </Field>
        </SettingsSectionCard>
      )}

      {vm.isAuthorisedProfileReady && vm.isBucketMode && (
        <SettingsSectionCard
          sectionOffset={sectionOffset + 1}
          title={t(
            "board.connections.catalogue.sourceSettings.msproject4tw.sectionBuckets",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.bucketsToLoadLabelText}
            hint={text.bucketsToLoadHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedBucketIds}
              query={bucketsQuery}
              placeholder={text.bucketsToLoadPlaceholderText}
              options={vm.buckets.map((bucket) => ({
                id: bucket.msdyn_projectbucketid,
                label: bucket.msdyn_name ?? bucket.msdyn_projectbucketid,
              }))}
              onQueryChange={setBucketsQuery}
              onSelectionChange={vm.onSelectBuckets}
              minQueryLength={0}
              typeToSearchText={text.bucketsToLoadHintText}
              isLoading={!vm.bucketsLoaded}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.doneBucketLabelText}
            hint={text.doneBucketHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedDoneBucketId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedDoneBucketId
              }
              selectedLabel={
                vm.buckets.find(
                  (bucket) =>
                    bucket.msdyn_projectbucketid === vm.selectedDoneBucketId,
                )?.msdyn_name
              }
              query={doneBucketQuery}
              placeholder={text.doneBucketPlaceholderText}
              options={vm.buckets.map((bucket) => ({
                id: bucket.msdyn_projectbucketid,
                label: bucket.msdyn_name ?? bucket.msdyn_projectbucketid,
              }))}
              onQueryChange={setDoneBucketQuery}
              onSelect={vm.onSelectDoneBucket}
              clearSelectionText={text.doneBucketPlaceholderText}
              isLoading={!vm.bucketsLoaded}
            />
          </Field>
        </SettingsSectionCard>
      )}

      {vm.isAuthorisedProfileReady && !vm.projectsLoaded && (
        <Text>{text.loadingProjectsText}</Text>
      )}
      {vm.isAuthorisedProfileReady && vm.isBucketMode && !vm.bucketsLoaded && (
        <Text>{text.loadingBucketsText}</Text>
      )}
      {vm.isAuthorisedProfileReady && vm.hasError && (
        <Text>{vm.errorMessage ?? text.loadErrorText}</Text>
      )}
    </>
  );
};
